import isDev from '@de/shared/utils/isDev';
import { sanitizeTelemetryAttributeValue } from './sanitizeOutboundUrlForTelemetry';

type NewRelicAction = 'noticeError' | 'setCustomAttribute' | 'addPageAction';
type NewRelicAttributes = Record<string, string | number | boolean>;

/**
 * Central seam for every client-side New Relic call in
 * `dynamic-experiences`. Modeled on the legacy supersonic
 * `se-supersonic-ui/src/utils/newRelicActions.ts` so both renderers share
 * the same shape, but tagged with `dynamicExperiencesError` (vs the
 * supersonic `supersonicUIError`) so the two surfaces stay distinguishable
 * in New Relic.
 *
 * Guards live here — not at each call site — so callers never re-implement
 * the SSR check or the "agent might be absent" fallback:
 *   - SSR no-op: `window` is undefined during server render, so exit early.
 *   - Dev logging: mirror the action + args to the console (grouped) on
 *     local hosts so failures are visible without a New Relic session.
 *   - Console fallback: when the host has not initialized `window.newrelic`
 *     the failure still lands in the console rather than being dropped.
 */
function resolveAgent(action: string, args: Array<unknown>) {
  if (typeof window === 'undefined') {
    return undefined;
  }

  if (isDev()) {
    console.groupCollapsed(
      '%cNewRelicEvent:',
      'background: #16a085; color: #fff; padding: 3px; font-weight: 700;',
      action,
    );
    console.log('action: ', action);
    console.log('args: ', args);
    console.groupEnd();
  }

  const agent = window.newrelic;
  if (!agent) {
    console.error(`New Relic Action Error: ${action}`, args);
    return undefined;
  }

  return agent;
}

function sanitizeAttributes(
  attributes?: NewRelicAttributes,
): NewRelicAttributes | undefined {
  if (!attributes) {
    return attributes;
  }
  const sanitized: NewRelicAttributes = {};
  for (const [key, value] of Object.entries(attributes)) {
    const next = sanitizeTelemetryAttributeValue(value);
    if (next === undefined) {
      continue;
    }
    sanitized[key] = next;
  }
  return sanitized;
}

function callNewRelic(action: NewRelicAction, args: Array<unknown>): void {
  const agent = resolveAgent(action, args);
  if (!agent) {
    return;
  }

  (agent[action] as (...callArgs: Array<unknown>) => void)(...args);
}

/**
 * Report a caught error to New Relic with an automatic
 * `dynamicExperiencesError: 'true'` tag so DE failures can be isolated in
 * monitoring. `context` carries call-site breadcrumbs (source, component
 * stack, etc.).
 */
export function newRelicNoticeError(
  error: Error,
  context?: NewRelicAttributes,
): void {
  callNewRelic('noticeError', [
    error,
    { ...sanitizeAttributes(context), dynamicExperiencesError: 'true' },
  ]);
}

/**
 * Set a custom New Relic attribute. Provided for parity with the legacy
 * seam; kept alongside `newRelicNoticeError` so future DE instrumentation
 * routes custom attributes through the same guarded path.
 */
export function newRelicCustomAttribute(
  name: string,
  value: string | number,
): void {
  const sanitized = sanitizeTelemetryAttributeValue(value);
  if (sanitized === undefined) {
    return;
  }
  callNewRelic('setCustomAttribute', [name, sanitized]);
}

/**
 * Emit a browser `PageAction`. Used for step-progression events so funnel
 * questions ("how many users reach step 3?") are answerable in NRQL without
 * inferring progression from page views.
 */
export function newRelicPageAction(
  name: string,
  attributes?: NewRelicAttributes,
): void {
  callNewRelic('addPageAction', [name, sanitizeAttributes(attributes)]);
}

/**
 * Name the current browser interaction so `BrowserInteraction` events record
 * step-to-step navigation instead of a default URL pattern.
 *
 * Unlike the actions above, `interaction()` returns a handle rather than acting
 * directly, so it can't route through `callNewRelic`. A DE step change is a
 * client-side state change rather than a route change, so the agent may hand
 * back an already-finished handle; every call is therefore optional-chained and
 * a failure here must never interrupt a step transition.
 */
export function newRelicInteractionName(name: string): void {
  const agent = resolveAgent('interaction', [name]);
  if (!agent) {
    return;
  }

  try {
    const interaction = agent.interaction?.();
    interaction?.setName?.(name)?.save?.();
  } catch (error) {
    console.error('New Relic Action Error: interaction', name, error);
  }
}
